<template>
  <v-container fluid class="pa-6">
    <!-- Row 1: Toggle + Health Check -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card class="modern-card" elevation="0">
          <v-card-title class="px-6 py-4">
            <v-icon left color="accent" class="mr-3">fas fa-toggle-on</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Configuración de Integración</span>
          </v-card-title>

          <v-divider />

          <v-card-text class="px-6 py-5">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="font-weight-bold primary--text body-1">Integración Relbase</div>
                <div class="text-caption grey--text mt-1">
                  Habilita funciones de facturación electrónica y mapeo de vendedores
                </div>
              </div>
              <v-switch
                v-model="config.integration_enabled"
                color="accent"
                :loading="saving"
                :disabled="loading"
                @change="saveConfig"
                inset
                hide-details
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="modern-card" elevation="0">
          <v-card-title class="px-6 py-4">
            <v-icon left color="accent" class="mr-3">fas fa-heartbeat</v-icon>
            <span class="text-h6 font-weight-bold primary--text">Estado de Conexión</span>
          </v-card-title>

          <v-divider />

          <v-card-text class="px-6 py-5">
            <div class="d-flex align-center">
              <v-icon
                :color="healthStatusColor"
                class="mr-3"
                size="20"
              >
                {{ healthStatusIcon }}
              </v-icon>
              <div>
                <div class="font-weight-bold" :class="healthStatusColor + '--text'">
                  {{ healthStatusText }}
                </div>
                <div class="text-caption grey--text mt-1">
                  {{ healthMessage }}
                </div>
              </div>
              <v-spacer />
              <v-btn
                outlined small
                color="accent"
                :loading="checkingHealth"
                @click="checkHealth"
                class="text-none"
              >
                <v-icon left x-small>fas fa-sync</v-icon>
                Verificar
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Row 2: Sync cards per catalog -->
    <v-row class="mt-2">
      <v-col cols="12" md="6" lg="3" v-for="cat in catalogs" :key="cat.key">
        <sync-catalog-card
          :title="cat.title"
          :icon="cat.icon"
          :sync-in-progress="config[cat.progressField]"
          :last-sync="config[cat.lastSyncField]"
          :disabled="!config.integration_enabled"
          @sync="syncCatalog(cat.key)"
        />
      </v-col>
    </v-row>

    <!-- Row 3: Sync All -->
    <v-row class="mt-2">
      <v-col cols="12">
        <div class="d-flex align-center">
          <v-btn
            color="accent"
            :loading="anySyncInProgress"
            :disabled="!config.integration_enabled || anySyncInProgress"
            @click="syncAll"
            class="text-none"
          >
            <v-icon left small>fas fa-sync</v-icon>
            Sincronizar Todo
          </v-btn>
          <span v-if="anySyncInProgress" class="ml-3 text-caption accent--text">
            Sincronización en curso...
          </span>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {
  fetchRelbaseConfig,
  updateRelbaseConfig,
  relbaseHealthCheck,
  syncRelbaseCatalogs,
  syncRelbaseCatalog,
} from '@/api/relbase'
import SyncCatalogCard from '@/components/relbase/SyncCatalogCard.vue'
import notifications from '@/utils/notifications'

export default {
  name: 'RelbaseSettings',
  components: { SyncCatalogCard },
  data() {
    return {
      loading: false,
      saving: false,
      checkingHealth: false,
      pollTimer: null,
      config: {
        integration_enabled: false,
        sync_geography_in_progress: false,
        last_geography_sync: null,
        sync_categories_in_progress: false,
        last_categories_sync: null,
        sync_sellers_in_progress: false,
        last_sellers_sync: null,
        sync_payment_methods_in_progress: false,
        last_payment_methods_sync: null,
        sync_warehouses_in_progress: false,
        last_warehouses_sync: null,
        sync_sale_channels_in_progress: false,
        last_sale_channels_sync: null,
      },
      catalogs: [
        { key: 'geography', title: 'Geografía', icon: 'fas fa-map-marker-alt', progressField: 'sync_geography_in_progress', lastSyncField: 'last_geography_sync' },
        { key: 'categories', title: 'Categorías', icon: 'fas fa-tags', progressField: 'sync_categories_in_progress', lastSyncField: 'last_categories_sync' },
        { key: 'sellers', title: 'Vendedores', icon: 'fas fa-users', progressField: 'sync_sellers_in_progress', lastSyncField: 'last_sellers_sync' },
        { key: 'payment-methods', title: 'Formas de Pago', icon: 'fas fa-credit-card', progressField: 'sync_payment_methods_in_progress', lastSyncField: 'last_payment_methods_sync' },
        { key: 'warehouses', title: 'Bodegas', icon: 'fas fa-warehouse', progressField: 'sync_warehouses_in_progress', lastSyncField: 'last_warehouses_sync' },
        { key: 'sale-channels', title: 'Canales de Venta', icon: 'fas fa-store', progressField: 'sync_sale_channels_in_progress', lastSyncField: 'last_sale_channels_sync' },
      ],
      healthStatus: 'unknown',
      healthMessage: 'No verificado',
    }
  },
  computed: {
    anySyncInProgress() {
      return this.catalogs.some(cat => this.config[cat.progressField])
    },
    healthStatusColor() {
      const map = { ok: 'success', disabled: 'warning', error: 'error', unknown: 'grey' }
      return map[this.healthStatus] || 'grey'
    },
    healthStatusIcon() {
      const map = {
        ok: 'fas fa-check-circle',
        disabled: 'fas fa-pause-circle',
        error: 'fas fa-times-circle',
        unknown: 'fas fa-question-circle',
      }
      return map[this.healthStatus] || 'fas fa-question-circle'
    },
    healthStatusText() {
      const map = {
        ok: 'Conectado',
        disabled: 'Deshabilitado',
        error: 'Error de Conexión',
        unknown: 'Sin Verificar',
      }
      return map[this.healthStatus] || 'Desconocido'
    },
  },
  async mounted() {
    await this.loadConfig()
    if (this.anySyncInProgress) {
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async loadConfig() {
      this.loading = true
      try {
        this.config = await fetchRelbaseConfig()
      } catch (e) {
        notifications.showError('Error al cargar la configuración')
      } finally {
        this.loading = false
      }
    },
    async saveConfig() {
      this.saving = true
      try {
        await updateRelbaseConfig({
          integration_enabled: this.config.integration_enabled,
        })
        notifications.showSuccess(
          this.config.integration_enabled
            ? 'Integración Relbase activada'
            : 'Integración Relbase desactivada'
        )
      } catch (e) {
        notifications.showError('Error al guardar la configuración')
        this.config.integration_enabled = !this.config.integration_enabled
      } finally {
        this.saving = false
      }
    },
    async syncCatalog(catalogKey) {
      try {
        await syncRelbaseCatalog(catalogKey)
        const cat = this.catalogs.find(c => c.key === catalogKey)
        if (cat) this.config[cat.progressField] = true
        this.startPolling()
      } catch (e) {
        if (e.response?.status === 409) {
          this.startPolling()
        } else {
          notifications.showError('Error al iniciar la sincronización')
        }
      }
    },
    async syncAll() {
      try {
        await syncRelbaseCatalogs()
        this.catalogs.forEach(cat => {
          this.config[cat.progressField] = true
        })
        this.startPolling()
      } catch (e) {
        if (e.response?.status === 409) {
          this.startPolling()
        } else {
          notifications.showError('Error al iniciar la sincronización')
        }
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        try {
          const data = await fetchRelbaseConfig()
          this.catalogs.forEach(cat => {
            this.config[cat.progressField] = data[cat.progressField]
            this.config[cat.lastSyncField] = data[cat.lastSyncField]
          })
          if (!this.anySyncInProgress) {
            this.stopPolling()
            notifications.showSuccess('Sincronización completada')
          }
        } catch (e) {
          // silently retry
        }
      }, 5000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async checkHealth() {
      this.checkingHealth = true
      try {
        const data = await relbaseHealthCheck()
        this.healthStatus = data.status
        this.healthMessage = data.message
      } catch (e) {
        this.healthStatus = 'error'
        this.healthMessage = e.response?.data?.message || 'No se pudo conectar con Relbase'
      } finally {
        this.checkingHealth = false
      }
    },
  },
}
</script>
